import { cn, Divider, Stack } from 'component-library';
import i18next from 'i18next';
import { FC, HTMLAttributes, useRef } from 'react';
import { useTranslation } from 'react-i18next';
import { NameType, ValueType } from 'recharts/types/component/DefaultTooltipContent';

import { PlotReportFactLabel, PlotReportFactLabelNameEnum, ValueClass } from '@/api/rest/resources/types/fact';
import { INDICATOR } from '@/api/rest/resources/types/IndicatorProduct';
import { UnitEnum } from '@/api/rest/resources/types/units';
import { getDisplayNumber } from '@/hooks/useDisplayNumber';
import { Logger } from '@/lib/logs/logger';
import { hasLabel } from '@/pages/shared/hooks/utils';
import { isNullish } from '@/utils/booleans';
import { formatUnit } from '@/utils/formatting';
import { printMonthYear } from '@/utils/formatting/date';
import { getColorForIndicator } from '@/utils/getColorForIndicator';

import { ValueClassPill } from '../Pill/ValueClassPill';
import { Area, Brush, Chart, ComposedChart, TooltipProps } from './components/Chart';
import { ChartLegendBar } from './components/LegendBar';
import {
  ChartTooltip,
  ChartTooltipDot,
  ChartTooltipTextPrimary,
  ChartTooltipTextSecondary,
} from './components/Tooltip';
import { ChartLegendBarItem } from './components/types';
import { useYAxisDomainFromMouseWheelZoom } from './hooks/useYAxisDomainFromMouseWheelZoom';
import { NoChartData } from './NoChartData';
import { ChartDataItem } from './ReferenceLines';
import { ChartProps, FactValue } from './types';
import { getChartDataFromGraphFact, getDataDefaultDomain, minMaxAxisDomain, sortChartData } from './utils';

const soilMoistureColors = getColorForIndicator(INDICATOR.SOIL_MOISTURE);
const precipitationColors = getColorForIndicator(INDICATOR.PRECIPITATION);

const chartColors = {
  precip: precipitationColors,
  sm: soilMoistureColors,
} as const;

const totalFactNames = {
  soil_moisture: 'sm',
  precipitation: 'precip',
};

const intrapolateNullValues = (data: ChartDataItem[]) => {
  const interpolatedData = structuredClone(data);
  const precipValues: Record<string, FactValue | null> = {};

  Object.entries(interpolatedData).forEach(([, currentData]) => {
    precipValues[currentData.label] = currentData.precip ?? null;
  });
  Object.entries(interpolatedData).forEach(([date, currentData]) => {
    const defaultPrecipValue = currentData.precip ?? precipValues[currentData.label];

    const hasInterpolatedLabels = hasLabel(
      { [PlotReportFactLabelNameEnum.value_class]: ValueClass.interpolated },
      currentData?.precip?.labels ?? [],
    );

    interpolatedData[Number(date)] = {
      ...interpolatedData[Number(date)],
      precip: {
        ...(defaultPrecipValue ?? null),
        hasInterpolatedLabels,
      },
    };
  });
  return interpolatedData;
};

export const SMPrecipitation: FC<HTMLAttributes<HTMLDivElement> & ChartProps> = ({ data, className, ...delegated }) => {
  const { t } = useTranslation();

  const chartContainerRef = useRef<HTMLDivElement>(null);
  const soilMoistureDefaultDomain = getDataDefaultDomain(
    data.filter((d) => d.name === 'soil_moisture'),
    0,
    1.2,
  );
  const yAxisDomainFromMouseWheel = useYAxisDomainFromMouseWheelZoom(chartContainerRef, soilMoistureDefaultDomain);

  const styles = cn('flex w-full flex-col', className);

  const factNames = totalFactNames;
  const dateDataMap = getChartDataFromGraphFact(data, factNames);
  const sortedChartData = sortChartData(dateDataMap, (date) => new Date(date).getTime());
  const intrapolatedData = intrapolateNullValues(sortedChartData);
  const chartData = intrapolatedData;

  const firstTick = chartData.at(0);
  const lastTick = chartData.at(-1);

  if (!firstTick || !lastTick) {
    Logger.error('Not enough data to render chart');
    return (
      <div className={cn(styles, 'h-full justify-center')} {...delegated}>
        <NoChartData />
      </div>
    );
  }

  const latestDate = new Date(lastTick.label);
  const sixMonthsAgo = new Date(latestDate.getFullYear() - 2, 1, 1);
  const startIndex = chartData.findIndex((item) => new Date(item.label) >= sixMonthsAgo);
  const endIndex = chartData.length - 1;

  return (
    <>
      <Chart.Container
        ref={chartContainerRef}
        {...delegated}
        data={chartData}
        config={{
          precip: {
            // eslint-disable-next-line sonarjs/no-duplicate-string
            label: t('shared.ncaDetail.details.water.labels.precipitation'),
            color: chartColors.precip.stroke,
          },
          sm: {
            // eslint-disable-next-line sonarjs/no-duplicate-string
            label: t('global.analysis.soilMoisture'),
            color: chartColors.sm.stroke,
          },
        }}
      >
        <ComposedChart data={chartData}>
          <Area
            yAxisId='left'
            isAnimationActive={false}
            type='monotone'
            dataKey='sm.value'
            stroke='var(--color-sm)'
            strokeWidth={2}
            fill='none'
            activeDot={ChartTooltipDot}
            connectNulls
          />
          <Area
            yAxisId='right'
            type='monotone'
            isAnimationActive={false}
            stroke='var(--color-precip)'
            strokeWidth={1}
            fill='var(--color-precip)'
            dataKey='precip.value'
            connectNulls
            activeDot={ChartTooltipDot}
          />
          <Chart.XAxis
            dataKey='label'
            domain={['dataMin', 'dataMax']}
            tickFormatter={printMonthYear}
            tickMargin={4}
            interval='preserveStart'
            type='number'
            scale='time'
            height={40}
          />
          <Chart.YAxis yAxisId='left' domain={yAxisDomainFromMouseWheel}>
            <Chart.Label>{formatUnit(UnitEnum['m^3'])}</Chart.Label>
          </Chart.YAxis>
          <Chart.YAxis
            yAxisId='right'
            orientation='right'
            tickMargin={4}
            domain={minMaxAxisDomain(0.97, 1.03)}
            tickFormatter={(value) => getDisplayNumber(value, i18next.language, { notation: 'compact' })}
          >
            <Chart.Label>{formatUnit(UnitEnum.m)}</Chart.Label>
          </Chart.YAxis>
          <Chart.Tooltip content={(props) => <CustomTooltip {...props} />} />
          <Brush
            dataKey='label'
            tickFormatter={printMonthYear}
            height={25}
            className='typography-caption text-text-secondary'
            stroke='#00000099'
            startIndex={startIndex}
            endIndex={endIndex}
          />
        </ComposedChart>
      </Chart.Container>
      <CustomLegendBar />
    </>
  );
};

const CustomLegendBar = () => {
  const { t } = useTranslation();

  const legendBarItems: ChartLegendBarItem[] = [
    {
      label: t('shared.ncaDetail.details.water.labels.precipitation'),
      color: chartColors.precip.fill,
    },
    {
      label: t('global.analysis.soilMoisture'),
      color: chartColors.sm.stroke,
    },
  ];

  return <ChartLegendBar items={legendBarItems} className='mt-8 sm:ml-[28px]' />;
};

const CustomTooltip = ({ active, payload, label }: TooltipProps<ValueType, NameType>) => {
  const { t } = useTranslation();

  const smValue = payload?.find((data) => data.dataKey === 'sm.value');
  const precipValue = payload?.find((data) => data.dataKey === 'precip.value');

  const smDisplay = !isNullish(smValue?.value)
    ? `${getDisplayNumber(smValue?.value as string, window.navigator.language)} ${formatUnit(UnitEnum['m^3'])}`
    : t('global.analysis.noData');
  const precipDisplay = !isNullish(precipValue?.value)
    ? `${getDisplayNumber(precipValue?.value as string, window.navigator.language)} ${formatUnit(UnitEnum.m)}`
    : t('global.analysis.noData');

  const dataPointHasLabel = precipValue && precipValue?.payload?.precip?.labels?.length > 0;
  const dataPointValueClass = dataPointHasLabel
    ? precipValue.payload.precip?.labels?.find(
        (dataLabel: PlotReportFactLabel) => dataLabel.name === PlotReportFactLabelNameEnum.value_class,
      )
    : undefined;

  const dataLabel = dataPointValueClass?.value as ValueClass;

  if (!active) return null;
  return (
    <ChartTooltip>
      <Stack spacing={8} direction='row' className='justify-between'>
        <ChartTooltipTextSecondary>{printMonthYear(label)}</ChartTooltipTextSecondary>
        {dataLabel && <ValueClassPill title={dataLabel} />}
      </Stack>
      <Stack spacing={8} direction='row' className='justify-between'>
        <ChartTooltipTextPrimary>{`${t('shared.ncaDetail.details.water.labels.precipitation')}`}</ChartTooltipTextPrimary>
        <ChartTooltipTextPrimary>{precipDisplay}</ChartTooltipTextPrimary>
      </Stack>
      <Stack direction='col' spacing={1.5}>
        <Divider className='my-3' />
        <Stack spacing={8} direction='row' className='justify-between'>
          <ChartTooltipTextSecondary>{printMonthYear(label)}</ChartTooltipTextSecondary>
          {dataLabel && <ValueClassPill title={dataLabel} />}
        </Stack>
        <Stack spacing={8} direction='row' className='justify-between'>
          <ChartTooltipTextPrimary>{`${t('global.analysis.soilMoisture')}`}</ChartTooltipTextPrimary>
          <ChartTooltipTextPrimary>{smDisplay}</ChartTooltipTextPrimary>
        </Stack>
      </Stack>
    </ChartTooltip>
  );
};

export { CustomTooltip as CustomTooltipWaterHistorical };
